import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { Button } from '../UI/Button';
import { Card } from '../UI/Card';

interface SendModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableBalance: number;
}

export const SendModal: React.FC<SendModalProps> = ({ isOpen, onClose, availableBalance }) => {
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [errors, setErrors] = useState<{ recipient?: string; amount?: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  const validateAlgorandAddress = (address: string): boolean => {
    // Algorand addresses are 58 characters long and contain only uppercase letters and numbers 2-7
    const algorandAddressRegex = /^[A-Z2-7]{58}$/;
    return algorandAddressRegex.test(address.trim());
  };

  const validateForm = () => {
    const newErrors: { recipient?: string; amount?: string } = {};

    if (!recipient.trim()) {
      newErrors.recipient = 'Recipient address is required';
    } else if (!validateAlgorandAddress(recipient.trim())) {
      newErrors.recipient = 'Invalid Algorand address format (58 characters, A-Z and 2-7)';
    }

    if (!amount.trim()) {
      newErrors.amount = 'Amount is required';
    } else {
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount) || numAmount <= 0) {
        newErrors.amount = 'Amount must be greater than 0';
      } else if (numAmount > availableBalance) {
        newErrors.amount = 'Insufficient balance';
      } else if (numAmount < 0.001) {
        newErrors.amount = 'Minimum amount is 0.001 ALGO';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSend = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Reset form and close modal
      setRecipient('');
      setAmount('');
      setErrors({});
      onClose();
      
      // Show success message
      alert(`Successfully sent ${amount} ALGO to ${recipient.slice(0, 8)}...${recipient.slice(-8)}`);
    } catch (error) {
      alert('Transaction failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMaxAmount = () => {
    const maxSendable = Math.max(0, availableBalance - 0.001); // Reserve for network fee
    setAmount(maxSendable.toString());
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Send ALGO</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recipient Address
            </label>
            <input
              type="text"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="Enter 58-character Algorand address"
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm ${
                errors.recipient ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.recipient && (
              <div className="flex items-center mt-1 text-red-600 text-sm">
                <AlertCircle size={14} className="mr-1" />
                {errors.recipient}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Amount (ALGO)
              </label>
              <button
                onClick={handleMaxAmount}
                className="text-xs text-primary-600 hover:text-primary-700"
              >
                Max
              </button>
            </div>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.000000"
              step="0.000001"
              min="0"
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.amount ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.amount && (
              <div className="flex items-center mt-1 text-red-600 text-sm">
                <AlertCircle size={14} className="mr-1" />
                {errors.amount}
              </div>
            )}
            <p className="text-sm text-gray-500 mt-1">
              Available: {availableBalance.toFixed(6)} ALGO
            </p>
          </div>

          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Amount</span>
              <span className="font-medium">{amount || '0'} ALGO</span>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Network Fee</span>
              <span className="font-medium">0.001 ALGO</span>
            </div>
            <div className="border-t border-gray-200 pt-1 mt-1">
              <div className="flex justify-between text-sm font-semibold">
                <span className="text-gray-900">Total</span>
                <span className="text-gray-900">
                  {amount ? (parseFloat(amount) + 0.001).toFixed(6) : '0.001'} ALGO
                </span>
              </div>
            </div>
          </div>

          <div className="flex space-x-3">
            <Button variant="outline" onClick={onClose} fullWidth disabled={isLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleSend} 
              fullWidth 
              disabled={isLoading || !recipient || !amount}
            >
              {isLoading ? 'Sending...' : 'Send ALGO'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};